import { useState } from 'react'
import { Link, Outlet, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/use-auth'
import { useVersion } from '../hooks/use-version'
import { useAlertSummary, rollupBadge } from '../hooks/use-alert-summary'
import type { NavBadge } from '@alpha-edge/shared'
import { useFeatures, type Features } from '../hooks/use-features'
import { colors } from './ui/styles'
import { useBranding } from '../hooks/use-branding'
import { useAccessionCount } from '../hooks/use-accession-count'
import { type UserRole, roleSatisfies } from '@alpha-edge/shared'
import medflowLogo from '../assets/img/MedFlow-Alpha-LIS.png'
import { UpgradeRequiredBanner } from './UpgradeRequiredBanner'
import { ImpersonationBanner } from './ImpersonationBanner'
import { NotificationBell } from './NotificationBell'
import { SupportRequestModal } from './SupportRequestModal'
import { ReleaseHistoryModal } from './ReleaseHistoryModal'
import { useReleaseAnnounce } from '../hooks/use-release-announce'

// Reload that bypasses the HTTP cache for the SPA shell. Without the
// cache-bust query param, nginx's default cache headers can serve the
// same stale index.html + bundle back, so the build-time version would
// stay pinned and the banner would reappear. Pair with
// Cache-Control: no-cache on index.html in nginx.conf.
function forceFreshReload(): void {
  const url = new URL(window.location.href)
  url.searchParams.set('v', Date.now().toString(36))
  window.location.href = url.toString()
}

interface NavItem {
  readonly path: string
  readonly label: string
  readonly roles?: readonly UserRole[]
  readonly featureFlag?: keyof Features
  readonly children?: readonly NavItem[]
}

interface NavSection {
  readonly header: string
  readonly items: readonly NavItem[]
}

// Edge / portable deployment nav — lab workflows, records, and admin.
const EDGE_NAV_SECTIONS: readonly NavSection[] = [
  {
    header: 'Workflows',
    items: [
      { path: '/', label: 'Dashboard' },
      { path: '/accessioning', label: 'Accession', roles: ['admin', 'tech', 'lab_tech'] },
      { path: '/testing', label: 'Tests', roles: ['admin', 'tech', 'lab_tech'] },
      {
        path: '/qc',
        label: 'QC',
        roles: ['admin', 'tech', 'lab_tech'],
        featureFlag: 'qc',
        children: [
          { path: '/qc/review', label: 'Review' },
          { path: '/qc/lots', label: 'Lots' },
          { path: '/qc/levey-jennings', label: 'Charts' },
          { path: '/qc/configurations', label: 'Configurations' },
          { path: '/qc/programs', label: 'Programs' },
        ],
      },
    ],
  },
  {
    header: 'Records',
    items: [
      { path: '/patients', label: 'Patients' },
      { path: '/orders', label: 'Orders', roles: ['admin', 'tech', 'lab_tech', 'provider'] },
      { path: '/results', label: 'Results' },
      {
        path: '/devices',
        label: 'Devices',
        roles: ['admin', 'tech', 'lab_tech'],
        children: [{ path: '/devices/errors', label: 'Error Log', roles: ['admin', 'tech'] }],
      },
    ],
  },
  {
    header: 'Admin',
    items: [
      { path: '/settings', label: 'Settings', roles: ['admin'] },
      { path: '/admin/ehr-integrations', label: 'EHR Integrations', roles: ['admin'], featureFlag: 'ehr' },
      { path: '/admin/logs', label: 'System Logs', roles: ['admin'] },
      { path: '/admin/events', label: 'Events', roles: ['admin', 'customer_admin'] },
      { path: '/admin/audit', label: 'Audit Trail', roles: ['admin', 'customer_admin'] },
      { path: '/admin/support-access-report', label: 'Support Access Report', roles: ['admin'] },
      { path: '/admin/medflow-staff', label: 'Staff Access', roles: ['admin'] },
      {
        path: '/licenses',
        label: 'My Environments',
        roles: ['customer_admin'],
        children: [{ path: '/licenses/alerts', label: 'Alerts' }],
      },
      {
        path: '/admin/secrets',
        label: 'Credential Vault',
        roles: ['admin', 'customer_admin'],
      },
      {
        path: '/admin/archive',
        label: 'Archive & Retention',
        roles: ['admin', 'customer_admin'],
      },
      { path: '/my-organization', label: 'My Organization', roles: ['customer_admin'] },
      { path: '/my-profile', label: 'My Profile' },
    ],
  },
]

// Central deployment nav — fleet management, staff access, and analytics
// replace the lab Workflows/Records sections that don't apply here.
const CENTRAL_NAV_SECTIONS: readonly NavSection[] = [
  {
    header: 'Staff Access',
    items: [
      { path: '/fleet/staff-grants', label: 'Staff Grants', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/my-edges', label: 'My Active Edges', roles: ['medflow_admin'] },
      {
        path: '/admin/remote-access',
        label: 'Remote Access',
        roles: ['customer_admin', 'medflow_admin'],
        featureFlag: 'remote_access',
      },
      { path: '/fleet/support-sessions', label: 'Support Sessions', roles: ['customer_admin', 'medflow_admin'] },
    ],
  },
  {
    header: 'Analytics',
    items: [
      { path: '/admin/meta-analytics', label: 'Meta Analytics', roles: ['medflow_admin'] },
      { path: '/admin/reports', label: 'Reports & Dashboards', roles: ['medflow_admin'] },
    ],
  },
  {
    header: 'Fleet',
    items: [
      { path: '/fleet', label: 'Overview', roles: ['medflow_admin'] },
      { path: '/fleet/alerts', label: 'Alerts', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/customers', label: 'Customers', roles: ['medflow_admin'] },
      { path: '/fleet/topology', label: 'Topology', roles: ['medflow_admin'] },
      { path: '/licenses', label: 'Environments', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/provision', label: 'Provision Node', roles: ['medflow_admin'] },
      { path: '/fleet/provisioning', label: 'Provision Instrument', roles: ['medflow_admin'] },
      {
        path: '/fleet/devices',
        label: 'Devices',
        roles: ['customer_admin', 'medflow_admin'],
        children: [{ path: '/devices/errors', label: 'Error Log', roles: ['medflow_admin'] }],
      },
      { path: '/fleet/backups', label: 'Backups', roles: ['medflow_admin'] },
      { path: '/fleet/migration', label: 'Migration', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/audit', label: 'Audit Log', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/fleet/local-audit', label: 'Local Audit Trail', roles: ['medflow_admin'] },
      { path: '/fleet/logs', label: 'Fleet Logs', roles: ['medflow_admin'] },
      { path: '/fleet/events', label: 'Events', roles: ['medflow_admin'] },
      { path: '/fleet/agent-suggestions', label: 'Agent Suggestions', roles: ['medflow_admin'] },
      { path: '/fleet/support-requests', label: 'Support Requests', roles: ['medflow_admin'] },
      { path: '/fleet/udm-rollouts', label: 'UDM Rollouts', roles: ['medflow_admin'] },
    ],
  },
  {
    header: 'Admin',
    items: [
      { path: '/admin/agent-tokens', label: 'Agent Tokens', roles: ['medflow_admin'] },
      { path: '/admin/external-api-keys', label: 'External API Keys', roles: ['medflow_admin'] },
      { path: '/admin/containers', label: 'Containers', roles: ['customer_admin', 'medflow_admin'] },
      { path: '/admin/fax-config', label: 'Fax Config', roles: ['medflow_admin'] },
      {
        path: '/admin/fax-import-logs',
        label: 'Fax Import Logs',
        roles: ['medflow_admin'],
        featureFlag: 'fax_ingest' as keyof Features,
      },
      { path: '/admin/offsite-backup', label: 'Backup Config', roles: ['medflow_admin'] },
      { path: '/admin/provisioning-config', label: 'Provisioning Config', roles: ['medflow_admin'] },
      { path: '/admin/rate-limit', label: 'API Rate Limit', roles: ['medflow_admin'] },
      {
        path: '/admin/analytics-sharing',
        label: 'Analytics Sharing',
        roles: ['customer_admin'],
        featureFlag: 'data_sharing_opt_out',
      },
      {
        path: '/admin/offsite-backup',
        label: 'Off-Site Backup',
        roles: ['customer_admin'],
        featureFlag: 'offsite_backup',
      },
      { path: '/my-organization', label: 'My Organization', roles: ['customer_admin'] },
      { path: '/my-profile', label: 'My Profile' },
    ],
  },
]

const SEVERITY_COLORS: Record<NavBadge['severity'], string> = {
  critical: '#dc2626',
  warning: '#f59e0b',
  info: colors.accent,
}

function NavBadgeSpan({ badge }: { badge: NavBadge }): JSX.Element | null {
  if (badge.count === 0) return null
  return (
    <span
      style={{
        display: 'inline-block',
        minWidth: '18px',
        height: '18px',
        lineHeight: '18px',
        borderRadius: '9px',
        background: SEVERITY_COLORS[badge.severity],
        color: '#fff',
        fontSize: '0.6875rem',
        fontWeight: 700,
        textAlign: 'center',
        padding: '0 4px',
        textTransform: 'none',
        letterSpacing: '0',
      }}
    >
      {badge.count}
    </span>
  )
}

const isPathActive = (itemPath: string, currentPath: string) => {
  if (itemPath === '/') return currentPath === '/'
  return currentPath.startsWith(itemPath)
}

const navLinkStyle = (isActive: boolean): React.CSSProperties => ({
  display: 'block',
  padding: '9px 15px',
  margin: '1px 10px',
  color: isActive ? '#0074d4' : '#525f7f',
  background: 'transparent',
  textDecoration: 'none',
  fontSize: '0.875rem',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  borderRadius: '3px',
  borderLeft: isActive ? '3px solid #0074d4' : '3px solid transparent',
  paddingLeft: isActive ? '12px' : '15px',
})

const subLinkStyle = (isActive: boolean): React.CSSProperties => ({
  ...navLinkStyle(isActive),
  paddingLeft: isActive ? '24px' : '27px',
  fontSize: '0.8125rem',
})

// §7.5.2: org branding in the top-right of the layout header. Renders
// the uploaded logo when set, otherwise shows a deployment-specific
// label: "MedFlow LIS Central Management" on central, or the org
// name on edge deployments.
function LayoutHeaderBrand(): JSX.Element {
  const { user } = useAuth()
  const branding = useBranding(user?.organizationId)
  const features = useFeatures()
  if (branding.logoUrl) {
    return <img src={branding.logoUrl} alt="Organization logo" style={{ maxHeight: 32, maxWidth: 180 }} />
  }
  const label = features.central ? 'MedFlow LIS Central Management' : (branding.orgName ?? 'MedFlow LIS')
  return <span style={{ color: colors.text, fontSize: '1.125rem', fontWeight: 600 }}>{label}</span>
}

export function Layout() {
  const { user, logout } = useAuth()
  const { buildTime: buildTimeVersion, runtime: runtimeVersion } = useVersion()
  const alertSummary = useAlertSummary()
  const accessionCount = useAccessionCount()
  const features = useFeatures()
  const location = useLocation()
  const [hoveredPath, setHoveredPath] = useState<string | null>(null)
  const [expandedSections, setExpandedSections] = useState<ReadonlySet<string>>(new Set())
  const [supportOpen, setSupportOpen] = useState(false)
  // Release-history modal (#236): opened either on demand (click on the
  // version footer -- full history) or once per version via
  // useReleaseAnnounce (compact preview). Both paths share one modal
  // instance; whichever triggered the open decides the previewCount.
  const [releaseHistoryManualOpen, setReleaseHistoryManualOpen] = useState(false)
  const [showReleaseAnnounce, dismissReleaseAnnounce] = useReleaseAnnounce(runtimeVersion)
  const releaseHistoryOpen = releaseHistoryManualOpen || showReleaseAnnounce
  const closeReleaseHistory = () => {
    setReleaseHistoryManualOpen(false)
    dismissReleaseAnnounce()
  }

  const role = user?.role as UserRole | undefined

  const navBadges: Record<string, NavBadge> = {}
  const accBadge: NavBadge = { count: accessionCount, severity: 'info' }
  if (accBadge.count > 0) navBadges['/accessioning'] = accBadge

  const { nav } = alertSummary
  if (nav.alerts.count > 0) {
    navBadges['/fleet/alerts'] = nav.alerts
    navBadges['/licenses/alerts'] = nav.alerts
  }
  if (nav.devices.count > 0) {
    navBadges['/fleet/devices'] = nav.devices
  }
  const fleetRollup = rollupBadge(nav.alerts, nav.devices)
  if (fleetRollup.count > 0) {
    navBadges['/fleet'] = fleetRollup
    navBadges['/licenses'] = fleetRollup
  }

  const isItemVisible = (item: NavItem) =>
    (!item.roles || (role && roleSatisfies(role, item.roles))) && (!item.featureFlag || features[item.featureFlag])

  const navSections = features.central ? CENTRAL_NAV_SECTIONS : EDGE_NAV_SECTIONS
  const visibleSections = navSections
    .map((section) => ({
      ...section,
      items: section.items.filter(isItemVisible),
    }))
    .filter((section) => section.items.length > 0)

  const getLinkBackground = (path: string, isActive: boolean) => {
    if (isActive) return colors.sidebarActive
    if (hoveredPath === path) return colors.sidebarHover
    return 'transparent'
  }

  const toggleSection = (path: string) => {
    const next = new Set(expandedSections)
    if (next.has(path)) next.delete(path)
    else next.add(path)
    setExpandedSections(next)
  }

  const isSectionExpanded = (path: string) => expandedSections.has(path) || isPathActive(path, location.pathname)

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      <aside
        data-testid="sidebar"
        style={{
          width: '280px',
          background: colors.sidebarBg,
          color: colors.sidebarText,
          padding: 0,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          borderRight: `1px solid ${colors.sidebarBorder}`,
        }}
      >
        {/* Logo */}
        <div
          style={{
            padding: '10px 15px 9px 15px',
            borderBottom: `1px solid ${colors.sidebarBorder}`,
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Link to="/">
            <img src={medflowLogo} alt="MedFlow" style={{ width: '200px' }} />
          </Link>
        </div>

        {/* Portal name */}
        <div
          style={{
            padding: '8px 15px',
            fontSize: '0.75rem',
            color: colors.textMuted,
            borderBottom: `1px solid ${colors.sidebarBorder}`,
          }}
        >
          {features.central ? 'Central Management' : 'MedFlow LIS'}
        </div>

        {/* Navigation */}
        <nav style={{ paddingTop: '6px', flex: 1, overflowY: 'auto' }}>
          {visibleSections.map((section, sectionIdx) => (
            <div key={section.header}>
              {sectionIdx > 0 && (
                <div style={{ height: '1px', background: colors.sidebarBorder, margin: '6px 15px' }} />
              )}
              <div
                style={{
                  padding: '8px 15px 4px',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  color: colors.textMuted,
                  textTransform: 'uppercase',
                  letterSpacing: '1px',
                }}
              >
                {section.header}
              </div>
              {section.items.map((item) => {
                const isActive = isPathActive(item.path, location.pathname)
                const hasChildren = item.children && item.children.length > 0
                const expanded = hasChildren && isSectionExpanded(item.path)

                return (
                  <div key={item.path}>
                    {hasChildren ? (
                      <div
                        style={{
                          ...navLinkStyle(isActive),
                          background: getLinkBackground(item.path, isActive),
                          cursor: 'pointer',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: 0,
                        }}
                        onMouseEnter={() => setHoveredPath(item.path)}
                        onMouseLeave={() => setHoveredPath(null)}
                      >
                        <Link
                          to={item.path}
                          onClick={(e) => {
                            if (!expanded) {
                              e.preventDefault()
                              toggleSection(item.path)
                            }
                          }}
                          style={{
                            flex: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            color: 'inherit',
                            textDecoration: 'none',
                            padding: '8px 12px',
                          }}
                        >
                          {item.label}
                          {navBadges[item.path] && <NavBadgeSpan badge={navBadges[item.path]} />}
                        </Link>
                        <span
                          role="button"
                          tabIndex={0}
                          onClick={(e) => {
                            e.stopPropagation()
                            toggleSection(item.path)
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault()
                              toggleSection(item.path)
                            }
                          }}
                          style={{
                            fontSize: '0.625rem',
                            color: colors.textMuted,
                            padding: '8px 12px',
                            cursor: 'pointer',
                          }}
                        >
                          {expanded ? '\u25B2' : '\u25BC'}
                        </span>
                      </div>
                    ) : (
                      <Link
                        to={item.path}
                        style={{
                          ...navLinkStyle(isActive),
                          background: getLinkBackground(item.path, isActive),
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                        }}
                        onMouseEnter={() => setHoveredPath(item.path)}
                        onMouseLeave={() => setHoveredPath(null)}
                      >
                        {item.label}
                        {navBadges[item.path] && <NavBadgeSpan badge={navBadges[item.path]} />}
                      </Link>
                    )}

                    {expanded &&
                      item.children?.map((child) => {
                        const childActive = location.pathname === child.path
                        return (
                          <Link
                            key={child.path}
                            to={child.path}
                            style={{
                              ...subLinkStyle(childActive),
                              background: getLinkBackground(child.path, childActive),
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                            }}
                            onMouseEnter={() => setHoveredPath(child.path)}
                            onMouseLeave={() => setHoveredPath(null)}
                          >
                            {child.label}
                            {navBadges[child.path] && <NavBadgeSpan badge={navBadges[child.path]} />}
                          </Link>
                        )
                      })}
                  </div>
                )
              })}
            </div>
          ))}
        </nav>

        {/* Version footer */}
        <div
          data-testid="version-footer"
          style={{
            padding: '10px 15px',
            fontSize: '0.6875rem',
            color: colors.textMuted,
            borderTop: `1px solid ${colors.sidebarBorder}`,
          }}
        >
          <div
            data-testid="version-text"
            role="button"
            tabIndex={0}
            onClick={() => setReleaseHistoryManualOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') setReleaseHistoryManualOpen(true)
            }}
            title="View release history"
            style={{ cursor: 'pointer' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = colors.accent
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = ''
            }}
          >
            Version {runtimeVersion}
          </div>
          {buildTimeVersion !== runtimeVersion && (
            <div
              data-testid="version-skew-warning"
              role="button"
              tabIndex={0}
              onClick={forceFreshReload}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') forceFreshReload()
              }}
              title={`Running ${buildTimeVersion}; a newer version (${runtimeVersion}) is available. Click to refresh.`}
              style={{
                marginTop: '4px',
                padding: '2px 6px',
                borderRadius: '3px',
                background: '#fff4d6',
                color: '#8a6d00',
                fontSize: '0.625rem',
                cursor: 'pointer',
                lineHeight: '1.3',
              }}
            >
              Click to refresh for the latest version
            </div>
          )}
        </div>
      </aside>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        {/* Header with blue top accent */}
        <div style={{ height: '2px', background: colors.accent, flexShrink: 0 }} />
        <header
          data-testid="header"
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '0 1.5rem',
            minHeight: '44px',
            borderBottom: '1px solid rgba(0,0,0,0.1)',
            background: '#fff',
            boxSizing: 'border-box',
          }}
        >
          <LayoutHeaderBrand />
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ color: colors.text, fontSize: '0.8125rem' }}>
              Logged in: <strong>{user?.firstName || user?.email}</strong>
            </span>
            <NotificationBell />
            <button
              onClick={() => setSupportOpen(true)}
              data-testid="support-button"
              style={{
                padding: '0.25rem 0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.accent,
                textDecoration: 'underline',
                fontSize: '0.8125rem',
              }}
            >
              Help
            </button>
            <Link
              to="/preferences"
              data-testid="preferences-link"
              style={{
                color: colors.accent,
                textDecoration: 'underline',
                fontSize: '0.8125rem',
              }}
            >
              Preferences
            </Link>
            <button
              onClick={logout}
              data-testid="logout-button"
              style={{
                padding: '0.25rem 0.5rem',
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: colors.accent,
                textDecoration: 'underline',
                fontSize: '0.8125rem',
              }}
            >
              Log out
            </button>
          </div>
        </header>

        <ImpersonationBanner />
        <UpgradeRequiredBanner />
        <main style={{ flex: 1, padding: '1rem 1.5rem', background: colors.bgPage }}>
          <Outlet />
        </main>
      </div>
      <SupportRequestModal isOpen={supportOpen} onClose={() => setSupportOpen(false)} />
      <ReleaseHistoryModal
        isOpen={releaseHistoryOpen}
        onClose={closeReleaseHistory}
        currentVersion={runtimeVersion}
        previewCount={releaseHistoryManualOpen ? undefined : 3}
      />
    </div>
  )
}
